/**
 * Single source of truth for country configuration across the monorepo.
 * Replaces the duplicated arrays in apps/web/src/lib/countries.ts,
 * apps/api/src/routes/countries.ts and apps/api/src/routes/meta.ts.
 *
 * Lowercase ISO 3166-1 alpha-2 codes throughout. Apps that need uppercase
 * (legacy API contracts, URL display) should `.toUpperCase()` at the edge.
 */

export const DEFAULT_COUNTRY = "ro";

/**
 * All ISO 3166-1 alpha-2 codes that the platform can render a country home
 * for. Matches the catalog seeded by `prisma/seed/iso-fillers-seed.ts` —
 * any code present in DB (live or `platformStatus = hidden`) must appear
 * here, otherwise apps/web's `/[locale]/[country]` page throws 404 instead
 * of routing the visitor to `ComingSoonHome` / WaitlistDialog.
 *
 * Curated active countries (RO, BG, DE, …) keep their full entries in
 * COUNTRY_DEFAULT_LOCALE / COUNTRY_FLAGS / COUNTRY_NAMES_EN below; the long
 * tail relies on fallbacks (`?? "en"`, `|| c.toUpperCase()`) at usage sites
 * and on DB-provided LocalizedString name + flagEmoji rendered server-side.
 */
const SUPPORTED_COUNTRIES_TUPLE = [
  "ad",
  "ae",
  "af",
  "ag",
  "ai",
  "al",
  "am",
  "ao",
  "aq",
  "ar",
  "as",
  "at",
  "au",
  "aw",
  "ax",
  "az",
  "ba",
  "bb",
  "bd",
  "be",
  "bf",
  "bg",
  "bh",
  "bi",
  "bj",
  "bl",
  "bm",
  "bn",
  "bo",
  "bq",
  "br",
  "bs",
  "bt",
  "bv",
  "bw",
  "by",
  "bz",
  "ca",
  "cc",
  "cd",
  "cf",
  "cg",
  "ch",
  "ci",
  "ck",
  "cl",
  "cm",
  "cn",
  "co",
  "cr",
  "cu",
  "cv",
  "cw",
  "cx",
  "cy",
  "cz",
  "de",
  "dj",
  "dk",
  "dm",
  "do",
  "dz",
  "ec",
  "ee",
  "eg",
  "eh",
  "er",
  "es",
  "et",
  "fi",
  "fj",
  "fk",
  "fm",
  "fo",
  "fr",
  "ga",
  "gb",
  "gd",
  "ge",
  "gf",
  "gg",
  "gh",
  "gi",
  "gl",
  "gm",
  "gn",
  "gp",
  "gq",
  "gr",
  "gs",
  "gt",
  "gu",
  "gw",
  "gy",
  "hk",
  "hm",
  "hn",
  "hr",
  "ht",
  "hu",
  "id",
  "ie",
  "il",
  "im",
  "in",
  "io",
  "iq",
  "ir",
  "is",
  "it",
  "je",
  "jm",
  "jo",
  "jp",
  "ke",
  "kg",
  "kh",
  "ki",
  "km",
  "kn",
  "kp",
  "kr",
  "kw",
  "ky",
  "kz",
  "la",
  "lb",
  "lc",
  "li",
  "lk",
  "lr",
  "ls",
  "lt",
  "lu",
  "lv",
  "ly",
  "ma",
  "mc",
  "md",
  "me",
  "mf",
  "mg",
  "mh",
  "mk",
  "ml",
  "mm",
  "mn",
  "mo",
  "mp",
  "mq",
  "mr",
  "ms",
  "mt",
  "mu",
  "mv",
  "mw",
  "mx",
  "my",
  "mz",
  "na",
  "nc",
  "ne",
  "nf",
  "ng",
  "ni",
  "nl",
  "no",
  "np",
  "nr",
  "nu",
  "nz",
  "om",
  "pa",
  "pe",
  "pf",
  "pg",
  "ph",
  "pk",
  "pl",
  "pm",
  "pn",
  "pr",
  "ps",
  "pt",
  "pw",
  "py",
  "qa",
  "re",
  "ro",
  "rs",
  "ru",
  "rw",
  "sa",
  "sb",
  "sc",
  "sd",
  "se",
  "sg",
  "sh",
  "si",
  "sj",
  "sk",
  "sl",
  "sm",
  "sn",
  "so",
  "sr",
  "ss",
  "st",
  "sv",
  "sx",
  "sy",
  "sz",
  "tc",
  "td",
  "tf",
  "tg",
  "th",
  "tj",
  "tk",
  "tl",
  "tm",
  "tn",
  "to",
  "tr",
  "tt",
  "tv",
  "tw",
  "tz",
  "ua",
  "ug",
  "um",
  "us",
  "uy",
  "uz",
  "va",
  "vc",
  "ve",
  "vg",
  "vi",
  "vn",
  "vu",
  "wf",
  "ws",
  "xk",
  "ye",
  "yt",
  "za",
  "zm",
  "zw",
] as const;

export type SupportedCountry = (typeof SUPPORTED_COUNTRIES_TUPLE)[number];

/**
 * Exported as `readonly string[]` (not the literal tuple) so callers can do
 * `SUPPORTED_COUNTRIES.includes(someString)` without TS2345 narrowing errors
 * on `string`. Use `SupportedCountry` for typed values; use `isSupportedCountry`
 * to narrow at the boundary.
 */
export const SUPPORTED_COUNTRIES: readonly string[] = SUPPORTED_COUNTRIES_TUPLE;

export function isSupportedCountry(value: string): value is SupportedCountry {
  return SUPPORTED_COUNTRIES.includes(value);
}

/**
 * Maps each supported country to its primary UI locale.
 * For countries whose native language isn't in the supported locale set,
 * we fall back to "en".
 */
export const COUNTRY_DEFAULT_LOCALE: Record<string, string> = {
  ro: "ro",
  bg: "bg",
  de: "de",
  hu: "hu",
  rs: "sr",
  hr: "hr",
  cz: "cs",
  sk: "en",
  si: "en",
  at: "de",
  ch: "de",
  nl: "en",
  be: "fr",
  pl: "pl",
  gr: "el",
  ie: "en",
  fr: "fr",
  it: "it",
  es: "es",
  lv: "en",
  lt: "en",
};

/**
 * Local domain mapping. Add entries here ONLY when a domain is purchased,
 * DNS configured, and the cert provisioned. An entry here drives middleware
 * redirects — listing a domain that doesn't exist breaks navigation.
 *
 * Keep in sync with the `local_domain` column in the `countries` DB table.
 */
export const DOMAIN_TO_COUNTRY: Record<string, string> = {
  // "pontimed.bg": "bg",
  // "pontimed.ro": "ro",
};

export const COUNTRY_TO_DOMAIN: Record<string, string> = Object.fromEntries(
  Object.entries(DOMAIN_TO_COUNTRY).map(([domain, country]) => [country, domain]),
);

export const GLOBAL_DOMAIN = "pontimed.app";

/**
 * Extract country code from a hostname (e.g. "pontimed.bg" → "bg").
 * Returns null for the global domain or localhost.
 */
export function getCountryFromHost(host: string | null): string | null {
  if (!host) return null;
  const hostname = host.split(":")[0];
  if (hostname === "localhost" || hostname === "127.0.0.1") return null;
  const clean = hostname.replace(/^www\./, "");
  return DOMAIN_TO_COUNTRY[clean] ?? null;
}

export function isLocalDomain(host: string | null): boolean {
  return getCountryFromHost(host) !== null;
}

/**
 * Neighboring countries for cross-border search. When the /find scope is
 * "cross", the Meilisearch query expands to include the current country
 * plus its neighbors (filtered to SUPPORTED_COUNTRIES).
 */
export const NEIGHBOR_COUNTRIES: Record<string, string[]> = {
  ro: ["bg", "hu", "rs", "ua", "md"],
  bg: ["ro", "rs", "gr", "tr"],
  de: ["at", "ch", "fr", "nl", "be", "pl", "cz"],
  hu: ["ro", "rs", "hr", "si", "at", "sk"],
  rs: ["ro", "bg", "hu", "hr"],
  hr: ["hu", "rs", "si", "it"],
  cz: ["de", "at", "sk", "pl"],
  sk: ["cz", "hu", "at", "pl"],
  si: ["at", "hu", "hr", "it"],
  at: ["de", "ch", "it", "si", "hu", "cz", "sk"],
  ch: ["de", "at", "fr", "it"],
  nl: ["de", "be"],
  be: ["nl", "de", "fr"],
  pl: ["de", "cz", "sk", "lt"],
  gr: ["bg"],
  ie: [],
  fr: ["de", "be", "ch", "it", "es"],
  it: ["fr", "ch", "at", "si", "hr"],
  es: ["fr"],
  lv: ["lt"],
  lt: ["lv", "pl"],
};

/**
 * Returns the current country plus its supported neighbors (deduped).
 * Used by /find scope=cross to build the Meilisearch country filter.
 */
export function getNeighborCountries(country: string): string[] {
  const code = country.toLowerCase();
  const neighbors = NEIGHBOR_COUNTRIES[code] ?? [];
  return [...new Set([code, ...neighbors].filter((c) => isSupportedCountry(c)))];
}

/** ISO 3166-1 alpha-2 → flag emoji */
export const COUNTRY_FLAGS: Record<string, string> = {
  ro: "\u{1F1F7}\u{1F1F4}",
  bg: "\u{1F1E7}\u{1F1EC}",
  de: "\u{1F1E9}\u{1F1EA}",
  hu: "\u{1F1ED}\u{1F1FA}",
  rs: "\u{1F1F7}\u{1F1F8}",
  hr: "\u{1F1ED}\u{1F1F7}",
  cz: "\u{1F1E8}\u{1F1FF}",
  sk: "\u{1F1F8}\u{1F1F0}",
  si: "\u{1F1F8}\u{1F1EE}",
  at: "\u{1F1E6}\u{1F1F9}",
  ch: "\u{1F1E8}\u{1F1ED}",
  nl: "\u{1F1F3}\u{1F1F1}",
  be: "\u{1F1E7}\u{1F1EA}",
  pl: "\u{1F1F5}\u{1F1F1}",
  gr: "\u{1F1EC}\u{1F1F7}",
  ie: "\u{1F1EE}\u{1F1EA}",
  fr: "\u{1F1EB}\u{1F1F7}",
  it: "\u{1F1EE}\u{1F1F9}",
  es: "\u{1F1EA}\u{1F1F8}",
  lv: "\u{1F1F1}\u{1F1FB}",
  lt: "\u{1F1F1}\u{1F1F9}",
};

/** English country names, used as a fallback when localized name is missing */
export const COUNTRY_NAMES_EN: Record<string, string> = {
  ro: "Romania",
  bg: "Bulgaria",
  de: "Germany",
  hu: "Hungary",
  rs: "Serbia",
  hr: "Croatia",
  cz: "Czech Republic",
  sk: "Slovakia",
  si: "Slovenia",
  at: "Austria",
  ch: "Switzerland",
  nl: "Netherlands",
  be: "Belgium",
  pl: "Poland",
  gr: "Greece",
  ie: "Ireland",
  fr: "France",
  it: "Italy",
  es: "Spain",
  lv: "Latvia",
  lt: "Lithuania",
};
